// AdminActivityScreen — admin-only activity dashboard
import React, { useEffect, useRef, useState } from 'react';
import { B } from '../constants';
import { useAuth } from '../AuthContext';
import { admin } from '../api';
import StatCard, { formatNumber } from '../components/StatCard';
import TimeseriesChart from '../components/TimeseriesChart';
import LoadingState from '../components/ui/LoadingState';
import ReelsStatsPanel from '../components/ReelsStatsPanel';

const RANGE_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

function formatDateTime(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function formatDate(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    return d.toISOString().slice(0, 10);
  } catch {
    return String(value);
  }
}

export default function AdminActivityScreen() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [errorOverview, setErrorOverview] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const [userActivityData, setUserActivityData] = useState(null);
  const [userActivityLoading, setUserActivityLoading] = useState(false);
  const [userActivityError, setUserActivityError] = useState(null);

  const [feedFilters, setFeedFilters] = useState({ surface: '', eventType: '' });
  const [feed, setFeed] = useState({ events: [], nextCursor: null });
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState(null);
  const [feedLoadingMore, setFeedLoadingMore] = useState(false);

  const userActivityReqRef = useRef(0);
  const searchReqRef = useRef(0);

  useEffect(() => () => {
    userActivityReqRef.current += 1;
    searchReqRef.current += 1;
  }, []);

  useEffect(() => {
    document.title = 'Activity — Admin | GreatBreak';
    return () => {
      document.title = 'GreatBreak — AI Travel Planner';
    };
  }, []);

  useEffect(() => {
    if (user?.isAdmin !== true) return undefined;
    let cancelled = false;
    setLoadingOverview(true);
    setErrorOverview(null);
    admin
      .overview(days)
      .then((data) => {
        if (cancelled) return;
        setOverview(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorOverview(err.message || 'Failed to load overview');
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingOverview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, user]);

  useEffect(() => {
    if (user?.isAdmin !== true) return undefined;
    let cancelled = false;
    setFeedLoading(true);
    setFeedError(null);
    const params = { limit: 50 };
    if (feedFilters.surface) params.surface = feedFilters.surface;
    if (feedFilters.eventType) params.eventType = feedFilters.eventType;
    admin
      .activity(params)
      .then((data) => {
        if (cancelled) return;
        setFeed({ events: data.events || [], nextCursor: data.nextCursor || null });
      })
      .catch((err) => {
        if (cancelled) return;
        setFeedError(err.message || 'Failed to load activity feed');
      })
      .finally(() => {
        if (cancelled) return;
        setFeedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [feedFilters.surface, feedFilters.eventType, user]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const reqId = ++searchReqRef.current;
    const q = searchQuery.trim();
    setSearchLoading(true);
    setSearchError(null);
    try {
      const res = await admin.searchUsers(q);
      if (reqId !== searchReqRef.current) return;
      setSearchResults(res.users || []);
    } catch (err) {
      if (reqId !== searchReqRef.current) return;
      setSearchError(err.message || 'Failed to search users');
      setSearchResults([]);
    } finally {
      if (reqId === searchReqRef.current) setSearchLoading(false);
    }
  };

  const selectUser = async (u) => {
    const reqId = ++userActivityReqRef.current;
    setSelectedUser(u);
    setUserActivityData(null);
    setUserActivityLoading(true);
    setUserActivityError(null);
    try {
      const res = await admin.userActivity(u.id, 200);
      if (reqId === userActivityReqRef.current) setUserActivityData(res);
    } catch (err) {
      if (reqId === userActivityReqRef.current) setUserActivityError(err.message || 'Failed to load user activity');
    } finally {
      if (reqId === userActivityReqRef.current) setUserActivityLoading(false);
    }
  };

  const loadMoreFeed = async () => {
    if (!feed.nextCursor || feedLoadingMore) return;
    setFeedLoadingMore(true);
    setFeedError(null);
    try {
      const params = { limit: 50, cursor: feed.nextCursor };
      if (feedFilters.surface) params.surface = feedFilters.surface;
      if (feedFilters.eventType) params.eventType = feedFilters.eventType;
      const data = await admin.activity(params);
      setFeed((prev) => ({
        events: [...prev.events, ...(data.events || [])],
        nextCursor: data.nextCursor || null,
      }));
    } catch (err) {
      setFeedError(err.message || 'Failed to load more');
    } finally {
      setFeedLoadingMore(false);
    }
  };

  if (!user) {
    return (
      <div style={{ padding: 40, textAlign: 'center', background: B.sandPattern, minHeight: '60vh' }}>
        <p style={{ color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>Please sign in</p>
      </div>
    );
  }

  if (user.isAdmin !== true) {
    return (
      <div style={{ padding: 40, textAlign: 'center', background: B.sandPattern, minHeight: '60vh' }}>
        <p style={{ color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>Admin access required</p>
      </div>
    );
  }

  const dailySeries = (overview?.daily || []).map((d) => ({
    date: formatDate(d.date),
    count: typeof d.count === 'number' ? d.count : Number(d.count || 0),
  }));

  const surfaceOptions = (overview?.surfaces || []).map((s) => s.surface).filter(Boolean);
  const eventTypeOptions = (overview?.topEvents || []).map((t) => t.eventType).filter(Boolean);

  return (
    <div style={{ background: B.sandPattern, minHeight: '100vh', padding: '40px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p style={{ fontFamily: "'Montserrat',sans-serif", letterSpacing: 2, fontSize: 12, fontWeight: 700, color: B.forest, marginBottom: 4, textTransform: 'uppercase' }}>
              Admin
            </p>
            <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 36, fontWeight: 700, color: B.night, margin: 0 }}>
              Activity Dashboard
            </h1>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setDays(r.value)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 20,
                  cursor: 'pointer',
                  border: `1px solid ${days === r.value ? B.forest : '#dcd6cc'}`,
                  background: days === r.value ? B.forest : '#fff',
                  color: days === r.value ? '#fff' : B.night,
                  fontFamily: "'Montserrat',sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {errorOverview && (
          <div style={{ background: 'rgba(220,53,69,0.1)', border: '1px solid rgba(220,53,69,0.25)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, color: '#dc3545', fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}>
            {errorOverview}
          </div>
        )}

        {loadingOverview ? (
          <LoadingState variant="spinner" message="Loading overview…" />
        ) : overview ? (
          <>
            <div
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                marginBottom: 24,
              }}
            >
              <StatCard label="DAU" value={overview.dau || 0} accent={B.forest} />
              <StatCard label="WAU" value={overview.wau || 0} accent={B.amber} />
              <StatCard label="MAU" value={overview.mau || 0} accent={B.sky} />
            </div>

            <ReelsStatsPanel />

            <section style={{ marginBottom: 32 }}>
              <h2 style={{ fontFamily: "'Playfair Display',serif", color: B.night, fontSize: 22, marginBottom: 12 }}>
                Daily events
              </h2>
              <TimeseriesChart series={dailySeries} metrics={[{ key: 'count', label: 'Events', color: B.forest }]} />
            </section>

            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', marginBottom: 32 }}>
              <section style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <h3 style={{ fontFamily: "'Playfair Display',serif", color: B.night, fontSize: 18, marginBottom: 12 }}>Surfaces</h3>
                <table style={{ width: '100%', fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', color: B.muted, fontWeight: 600, paddingBottom: 8 }}>Surface</th>
                      <th style={{ textAlign: 'right', color: B.muted, fontWeight: 600, paddingBottom: 8 }}>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(overview.surfaces || []).map((s, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '6px 0', color: B.night }}>{s.surface || '-'}</td>
                        <td style={{ padding: '6px 0', color: B.night, textAlign: 'right' }}>{formatNumber(s.count || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
              <section style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <h3 style={{ fontFamily: "'Playfair Display',serif", color: B.night, fontSize: 18, marginBottom: 12 }}>Top events</h3>
                <table style={{ width: '100%', fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', color: B.muted, fontWeight: 600, paddingBottom: 8 }}>Event type</th>
                      <th style={{ textAlign: 'right', color: B.muted, fontWeight: 600, paddingBottom: 8 }}>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(overview.topEvents || []).map((t, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '6px 0', color: B.night }}>{t.eventType || '-'}</td>
                        <td style={{ padding: '6px 0', color: B.night, textAlign: 'right' }}>{formatNumber(t.count || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            </div>
          </>
        ) : null}

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontFamily: "'Playfair Display',serif", color: B.night, fontSize: 22, marginBottom: 12 }}>User search</h2>
          <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, or username"
                style={{
                  flex: '1 1 240px',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid #dcd6cc',
                  fontFamily: "'Montserrat',sans-serif",
                  fontSize: 14,
                }}
              />
              <button
                type="submit"
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: B.forest,
                  color: '#fff',
                  cursor: 'pointer',
                  fontFamily: "'Montserrat',sans-serif",
                  fontWeight: 600,
                }}
              >
                Search
              </button>
            </form>
            {searchError && <div style={{ color: '#dc3545', fontSize: 13, marginBottom: 8 }}>{searchError}</div>}
            {searchLoading ? (
              <LoadingState variant="spinner" message="Searching…" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {searchResults.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => selectUser(u)}
                    style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: selectedUser?.id === u.id ? `1px solid ${B.forest}` : '1px solid #eee',
                      background: selectedUser?.id === u.id ? 'rgba(26,107,74,0.05)' : '#fff',
                      cursor: 'pointer',
                      fontFamily: "'Montserrat',sans-serif",
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <div style={{ color: B.night, fontWeight: 600 }}>{u.name || u.email}</div>
                        <div style={{ color: B.muted, fontSize: 12 }}>{u.email}{u.username ? ` · @${u.username}` : ''}</div>
                      </div>
                      <div style={{ color: B.muted, fontSize: 12, textAlign: 'right' }}>
                        <div>{formatNumber(u.totalEvents || 0)} events</div>
                        <div>{u.lastSeen ? `Last seen ${formatDateTime(u.lastSeen)}` : 'No events'}</div>
                      </div>
                    </div>
                  </button>
                ))}
                {searchResults.length === 0 && !searchLoading && <div style={{ color: B.muted, fontSize: 13 }}>No results</div>}
              </div>
            )}
          </div>
        </section>

        {selectedUser && (
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontFamily: "'Playfair Display',serif", color: B.night, fontSize: 22, marginBottom: 12 }}>Selected user</h2>
            <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                <div>
                  <div style={{ color: B.night, fontWeight: 700, fontFamily: "'Playfair Display',serif", fontSize: 18 }}>{userActivityData?.user?.name || selectedUser.name}</div>
                  <div style={{ color: B.muted, fontSize: 13 }}>{userActivityData?.user?.email || selectedUser.email}{(userActivityData?.user?.username || selectedUser.username) ? ` · @${userActivityData?.user?.username || selectedUser.username}` : ''}</div>
                </div>
                <div style={{ color: B.night, fontWeight: 600 }}>
                  Events shown (last {formatNumber(userActivityData?.totalEvents || 0)}):
                </div>
              </div>
              {userActivityError && <div style={{ color: '#dc3545', fontSize: 13, marginBottom: 8 }}>{userActivityError}</div>}
              {userActivityLoading ? (
                <LoadingState variant="spinner" message="Loading user activity…" />
              ) : userActivityData?.sessions?.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {userActivityData.sessions.map((session, idx) => (
                    <div key={idx} style={{ border: '1px solid #eee', borderRadius: 10, padding: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                        <div style={{ color: B.night, fontWeight: 600 }}>
                          {formatDateTime(session.startedAt)} — {formatDateTime(session.endedAt)}
                        </div>
                        <div style={{ color: B.muted, fontSize: 12 }}>{session.events?.length || 0} events</div>
                      </div>
                      {session.userAgent && <div style={{ color: B.muted, fontSize: 11, marginBottom: 8, wordBreak: 'break-word' }}>{session.userAgent}</div>}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {(session.events || []).map((ev) => (
                          <details key={ev.id} style={{ border: '1px solid #f0eee8', borderRadius: 8, padding: '6px 10px' }}>
                            <summary style={{ cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: B.night }}>
                              {ev.eventType} · {ev.surface || '-'} · {ev.path || '-'} · {formatDateTime(ev.createdAt)}
                            </summary>
                            <div style={{ marginTop: 6, fontSize: 12, color: B.muted }}>
                              <div>Session: {ev.sessionId || '-'}</div>
                              {ev.payload && Object.keys(ev.payload).length > 0 && (
                                <pre style={{ background: '#f9f7f3', padding: 8, borderRadius: 6, overflowX: 'auto', marginTop: 6 }}>
                                  {JSON.stringify(ev.payload, null, 2)}
                                </pre>
                              )}
                            </div>
                          </details>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: B.muted, fontSize: 13 }}>No events for this user</div>
              )}
            </div>
          </section>
        )}

        <section style={{ marginBottom: 40 }}>
          <h2 style={{ fontFamily: "'Playfair Display',serif", color: B.night, fontSize: 22, marginBottom: 12 }}>Recent global events</h2>
          <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <select
                value={feedFilters.surface}
                onChange={(e) => setFeedFilters((f) => ({ ...f, surface: e.target.value }))}
                style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #dcd6cc', fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}
              >
                <option value="">All surfaces</option>
                {surfaceOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <select
                value={feedFilters.eventType}
                onChange={(e) => setFeedFilters((f) => ({ ...f, eventType: e.target.value }))}
                style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #dcd6cc', fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}
              >
                <option value="">All events</option>
                {eventTypeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            {feedError && <div style={{ color: '#dc3545', fontSize: 13, marginBottom: 8 }}>{feedError}</div>}
            {feedLoading ? (
              <LoadingState variant="spinner" message="Loading feed…" />
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {feed.events.map((ev) => (
                    <div key={ev.id} style={{ border: '1px solid #f0eee8', borderRadius: 8, padding: '8px 10px', fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                        <div style={{ color: B.night }}>
                          <strong>{ev.eventType}</strong> · {ev.surface || '-'} · {ev.path || '-'}
                        </div>
                        <div style={{ color: B.muted }}>{formatDateTime(ev.createdAt)}</div>
                      </div>
                      <div style={{ color: B.muted, fontSize: 12 }}>
                        {ev.user?.name || ev.user?.email || (ev.userId ? `User ${ev.userId}` : 'Anonymous')}
                      </div>
                    </div>
                  ))}
                </div>
                {feed.events.length === 0 && <div style={{ color: B.muted, fontSize: 13 }}>No events</div>}
                {feed.nextCursor && (
                  <button
                    type="button"
                    onClick={loadMoreFeed}
                    disabled={feedLoadingMore}
                    style={{
                      marginTop: 12,
                      padding: '8px 14px',
                      borderRadius: 10,
                      border: '1px solid #dcd6cc',
                      background: '#fff',
                      cursor: feedLoadingMore ? 'not-allowed' : 'pointer',
                      fontFamily: "'Montserrat',sans-serif",
                      fontWeight: 600,
                    }}
                  >
                    {feedLoadingMore ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
